import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {ToastService} from "../../../services/toast.service";
import {FormBuilder, Validators} from "@angular/forms";
import {PasswordConfirmationValidator} from "../../../validators/passwordConfirmationValidator";
import {InvitedUsersService} from "../../../services/invited-users.service";
import {Origin} from "../../../enums/origin.enum";

@Component({
  selector: 'complete-registration',
  templateUrl: './complete-registration.component.html',
  styleUrl: './complete-registration.component.css',
  standalone: false
})
export class CompleteRegistrationComponent implements OnInit {
  registrationForm: any;
  private invitedUser: any;
  token: any;
  registrationCompleted: boolean = false;
  isPasswordShown: boolean = false;
  isConfirmPasswordShown: boolean = false;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly inviteService = inject(InvitedUsersService);

  constructor() {
    this.registrationForm = this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        company: [{value: '', disabled: true}],
        phone: [''],
        email: [{value: '', disabled: true}],
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      {validator: PasswordConfirmationValidator.validatePasswordMatch('password', 'confirmPassword')}
    );
  }

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token');
    this.inviteService.getInvitedUser(this.token).then((data: any) => {
      if (data.Error && data.Error.Code == 111) {
        this.toastService.showToast(data.Error.Error, 'error');
        this.router.navigate(['/sign-in']);
      }
      this.invitedUser = data.data;

      this.registrationForm.patchValue({
        firstName: this.invitedUser.first_name,
        lastName: this.invitedUser.last_name,
        company: this.invitedUser.company_name,
        phone: this.invitedUser.phone,
        email: this.invitedUser.work_email
      });
    }).catch(() => {
      this.toastService.showToast('Failed to load invitation details', 'error');
      this.router.navigate(['/sign-in']);
    });
  }

  isInvalidAndTouched(controlName: string): boolean {
    const control = this.registrationForm.get(controlName);
    return control != null && control.invalid && (control.dirty || control.touched);
  }

  register() {
    if (!this.registrationForm.valid) {
      this.registrationForm.markAllAsTouched();
      if (this.registrationForm.controls['confirmPassword'].errors?.['mustMatch']) {
        this.toastService.showToast('Passwords do not match.', 'error');
      } else {
        this.toastService.showToast('Please fill out all required fields.', 'error');
      }
      return;
    }

    const body = {
      InvitedUserId: this.invitedUser.cp_invited_user_id,
      FirstName: this.registrationForm.value.firstName,
      LastName: this.registrationForm.value.lastName,
      Email: this.invitedUser.work_email,
      Phone: this.registrationForm.value.phone || null,
      Password: this.registrationForm.value.password,
      HashedPassword: this.registrationForm.value.password,
      Token: this.token
    };

    this.inviteService.createUser(this.invitedUser.cp_invited_user_id, body).then(
      (res: any) => {
        this.registrationCompleted = true;
        this.toastService.showToast(res.message || 'Successfully completed registration', 'success');
        if (this.invitedUser.origin_id == Origin.RAMS) {
          this.router.navigate(['/sign-in']);
        }
      })
      .catch(() => this.toastService.showToast('Registration failed', 'error'));
  }

  toggleShowPassword() {
    this.isPasswordShown = !this.isPasswordShown;
  }

  toggleShowConfirmPassword() {
    this.isConfirmPasswordShown = !this.isConfirmPasswordShown;
  }
}
